import {
    CampaignIndicatorDefinition,
    LanguageIndicatorDefinition,
    MediaSegments,
} from "models/OrganizationModels/OrganizationMetadataConfiguration";
import {
    CampaignIndicatorType,
    ConcatFieldType,
    ConcatFieldValueType,
    ConditionIndicatorType,
    ConditionRuleAPIType,
    ConditionRuleType,
    CustomFieldType,
    LanguageIndicatorType,
    MappedValueType,
    MediaSegmentsType,
    SelectedTargetFields,
    SetupType,
    SourceFieldConfiguration,
    SourceFieldType,
    StatementAPIType,
    StatementType,
    TargetFieldType,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import {
    ConditionalRuleType,
    OrganizationMetadataFileType,
    sourceFieldsByType,
    TargetFieldKind,
} from "./constants";

import { OrganizationMetadataFileTypeExtension } from "models/OrganizationModels/OrganizationMetadataFileTypeExtension";

const formatConditionIndicators = (
    indicators: ConditionIndicatorType[] | undefined,
    sourceFields: SourceFieldType[],
): ConditionIndicatorType[] => {
    return (
        indicators?.map((indicator) => {
            const sourceField = sourceFields?.find(
                (field) => field.alias === indicator.sourceName,
            );
            return {
                id: indicator.id,
                compareType: indicator.compareType,
                valuePath: sourceField?.path,
                sourceName: indicator.sourceName || "",
                value: indicator.value,
                isRequired: sourceField?.isRequired || false,
                extractionPattern:
                    sourceField?.extractionPattern ??
                    indicator.extractionPattern ??
                    null,
                hasError: indicator.hasError,
            };
        }) || []
    );
};

const formatStatementForAPI = (
    statement: StatementType | undefined,
    sourceFields: SourceFieldType[],
): StatementAPIType | undefined => {
    if (!statement) {
        return undefined;
    }

    const sourceField = sourceFields?.find(
        (field) => field.alias === statement.sourceName,
    );

    return {
        id: statement.id,
        conditionIndicators: formatConditionIndicators(
            statement.conditionIndicators,
            sourceFields,
        ),
        concatRule:
            statement.result || statement.sourceName
                ? {
                      id: statement.id,
                      literalValue: statement.result,
                      isLiteral: statement.isLiteral || false,
                      sourceName: statement.sourceName,
                      expressionValue: !statement.isLiteral
                          ? sourceField?.path
                          : undefined,
                  }
                : undefined,
        hasCondition: !!statement.conditionIndicators?.length,
        then: formatStatementForAPI(statement.then, sourceFields),
        else: formatStatementForAPI(statement.else, sourceFields),
    };
};

const getTermFromConcatField = (
    field: ConcatFieldValueType,
    sourceFields: SourceFieldType[],
) => {
    const isSeparator = field.type === "separator";
    return {
        id: field.id,
        isLiteral: isSeparator,
        expressionValue: isSeparator
            ? undefined
            : sourceFields.find((e) => e.alias === field.value)?.path,
        literalValue: isSeparator ? field.value : undefined,
        sourceName: isSeparator ? undefined : field.value,
    };
};

const formatStatementFromMapped = (
    mappedValue: MappedValueType,
    index: number,
    sourceField?: SourceFieldConfiguration,
) => {
    const value = mappedValue.pairs[index];
    return {
        id: value.id,
        hasCondition: true,
        conditionIndicators: [
            {
                id: uuidv4(),
                compareType: 1,
                valuePath: sourceField?.expressionValuePath || "",
                value: value.sourceValue,
                isRequired: sourceField?.isRequired || false,
                extractionPattern: sourceField?.extractionPattern ?? null,
                sourceName: sourceField?.sourceName || "",
                hasError: false,
            },
        ],
        then: {
            id: uuidv4(),
            hasCondition: false,
            conditionIndicators: [],
            concatRule: {
                id: uuidv4(),
                literalValue: value.targetValue,
                isLiteral: true,
            },
        },
    };
};

const updateSourceFields = (
    sourceFields: SourceFieldConfiguration[],
    sourceField: SourceFieldConfiguration,
) => {
    const existingFieldIndex = sourceFields.findIndex(
        (field) => field.id === sourceField.id,
    );

    if (existingFieldIndex !== -1) {
        sourceFields[existingFieldIndex] = sourceField;
    } else {
        sourceFields.push(sourceField);
    }

    return sourceFields;
};

// No target means we only read the field, usually because a condition compares against it. Saying
// that out loud beats making the backend guess from a blank string.
const resolveTargetKind = (
    targetFieldName: string | undefined,
    isCustomField: boolean,
): TargetFieldKind => {
    if (!targetFieldName) {
        return TargetFieldKind.None;
    }
    return isCustomField ? TargetFieldKind.Extended : TargetFieldKind.Core;
};

export const getUpdatedSourceFields = (
    sourceFields: SourceFieldType[],
    shouldApplyConditions: boolean,
    conditionRules: ConditionRuleType[],
    shouldConcatenateFields: boolean,
    concatFields: ConcatFieldType[],
    mappedValues: MappedValueType[],
    customTargetFields: CustomFieldType[],
    targetFields: TargetFieldType[],
    selectedTargetFields: SelectedTargetFields,
) => {
    const getTargetFieldAndIsCustom = (sourceFieldId: string) => {
        const targetFieldId = selectedTargetFields.find(
            (f) => f.sourceFieldId === sourceFieldId,
        )?.targetFieldId;
        const customField = customTargetFields.find(
            (f) => f.id === targetFieldId,
        );
        const coreField = targetFields.find((f) => f.id === targetFieldId);
        return {
            targetMetaDataField: coreField?.value,
            isCustomField: !!customField,
            isIndexed: customField?.isIndexed ?? false,
        };
    };

    let updatedSourceFields: SourceFieldConfiguration[] = sourceFields.map(
        (field) => {
            const { targetMetaDataField, isCustomField, isIndexed } =
                getTargetFieldAndIsCustom(field.id);

            return {
                sourceName: field.alias,
                targetFieldName: field.targetFieldName,
                conditionalRuleType: null,
                id: field.id,
                isRequired: field.isRequired,
                extractionPattern: field.extractionPattern,
                expressionValuePath: field.path,
                conditionRule: undefined,
                isIndexed,
                dataType: 3,
                hasCondition: true,
                targetMetaDataField,
                isCustomField,
                targetKind: resolveTargetKind(
                    field.targetFieldName,
                    isCustomField,
                ),
            };
        })
        // Yes, we still send fields that are only used by conditional logic. They go out as
        // targetKind None and the backend throws them away, but they have to make the trip so the
        // path and extraction pattern survive a reload - fromAPI rebuilds these rows from the
        // condition indicators, see getSourcesFromStatement.
        .filter((field) => !!field.sourceName && !!field.expressionValuePath);

    if (shouldApplyConditions) {
        conditionRules.forEach((rule) => {
            const { targetMetaDataField, isCustomField, isIndexed } =
                getTargetFieldAndIsCustom(rule.id);
            const sourceField: SourceFieldConfiguration = {
                sourceName: rule.targetFieldName,
                targetFieldName: rule.targetFieldName,
                conditionalRuleType: ConditionalRuleType.Conditional,
                id: rule.id,
                isRequired: false,
                isIndexed,
                dataType: 0,
                hasCondition: true,
                targetMetaDataField,
                isCustomField,
                targetKind: resolveTargetKind(
                    rule.targetFieldName,
                    isCustomField,
                ),
                conditionRule: {
                    id: rule.id,
                    conditionIndicators: formatConditionIndicators(
                        rule.conditionIndicators,
                        sourceFields,
                    ),
                    then: formatStatementForAPI(
                        rule.then,
                        sourceFields,
                    ) as StatementAPIType,
                    else: formatStatementForAPI(rule.else, sourceFields),
                    hasCondition: !!rule.conditionIndicators?.length,
                },
            };
            updatedSourceFields = updateSourceFields(
                updatedSourceFields,
                sourceField,
            );
        });
    }

    if (shouldConcatenateFields) {
        concatFields.forEach((concatField) => {
            if (!concatField.fields.length) {
                return;
            }
            const { targetMetaDataField, isCustomField, isIndexed } =
                getTargetFieldAndIsCustom(concatField.id);
            const sourceField: SourceFieldConfiguration = {
                sourceName: concatField.targetFieldName,
                targetFieldName: concatField.targetFieldName,
                conditionalRuleType: ConditionalRuleType.Concatenation,
                id: concatField.id,
                isRequired: true,
                isIndexed,
                dataType: 0,
                hasCondition: true,
                targetMetaDataField,
                isCustomField,
                targetKind: resolveTargetKind(
                    concatField.targetFieldName,
                    isCustomField,
                ),
                conditionRule: {
                    id: concatField.id,
                    conditionIndicators: [],
                    hasCondition: false,
                    concatRule: getTermFromConcatField(
                        concatField.fields[0],
                        sourceFields,
                    ),
                },
            };
            let concatRule = sourceField.conditionRule?.concatRule;
            for (let i = 1; i < concatField.fields.length; i++) {
                const field = concatField.fields[i];
                const isLast = i === concatField.fields.length - 1;

                concatRule &&
                    (concatRule.rightTerm = getTermFromConcatField(
                        field,
                        sourceFields,
                    ));

                if (!isLast) {
                    concatRule = concatRule?.rightTerm;
                }
            }

            updatedSourceFields = updateSourceFields(
                updatedSourceFields,
                sourceField,
            );
        });
    }

    if (mappedValues?.length) {
        // remove mapped values without targets and without valid pairs
        const validMappedValues = mappedValues
            .filter((mappedValue) => mappedValue.targetField)
            .map((mappedValue) => ({
                ...mappedValue,
                pairs: mappedValue.pairs.filter(
                    (pair) => pair.sourceValue && pair.targetValue,
                ),
            }));

        validMappedValues.forEach((mappedValue) => {
            // Find the source field that contains the target field
            const existingSourceField = updatedSourceFields.find(
                (field) => field.targetFieldName === mappedValue.targetField,
            );

            const targetFieldName = existingSourceField?.targetFieldName || "";

            // Skip if the target field name is not found
            if (!targetFieldName) {
                return;
            }

            const { targetMetaDataField, isCustomField, isIndexed } =
                getTargetFieldAndIsCustom(mappedValue.id);

            const sourceField: SourceFieldConfiguration = {
                sourceName: targetFieldName,
                targetFieldName: targetFieldName,
                conditionalRuleType: ConditionalRuleType.ValueAssignment,
                id: mappedValue.id,
                isRequired: false,
                isIndexed,
                dataType: 0,
                hasCondition: false,
                targetMetaDataField,
                isCustomField,
                targetKind: resolveTargetKind(targetFieldName, isCustomField),
                conditionRule: {
                    ...formatStatementFromMapped(
                        mappedValue,
                        0,
                        existingSourceField,
                    ),
                },
            };

            let thenObj: StatementAPIType | ConditionRuleAPIType | undefined =
                sourceField.conditionRule;
            for (let i = 1; i < mappedValue.pairs.length; i++) {
                thenObj &&
                    (thenObj.else = formatStatementFromMapped(
                        mappedValue,
                        i,
                        existingSourceField,
                    ));
                thenObj = thenObj?.else;
            }

            updatedSourceFields = updateSourceFields(
                updatedSourceFields,
                sourceField,
            );
        });
    }

    return updatedSourceFields;
};

export const formatMediaSegmentsForAPI = (
    mediaSegments?: MediaSegmentsType,
    mediaSegmentsFromApi?: MediaSegments | null,
) => {
    let updatedMediaSegments: MediaSegments | undefined = undefined;

    if (mediaSegmentsFromApi && mediaSegments) {
        updatedMediaSegments = {
            ...mediaSegmentsFromApi,
            rootPath: mediaSegments.rootPath,
            start: {
                ...mediaSegmentsFromApi.start,
                valuePath: mediaSegments.start,
            },
            end: {
                ...mediaSegmentsFromApi.end,
                primary: {
                    ...mediaSegmentsFromApi.end.primary,
                    valuePath: mediaSegments.endPrimary,
                },
                alternate: {
                    ...mediaSegmentsFromApi.end.alternate,
                    valuePath: mediaSegments.endAlternate,
                },
            },
            agentName: {
                ...mediaSegmentsFromApi.agentName,
                valuePath: mediaSegments.agentName,
            },
            agentId: {
                ...mediaSegmentsFromApi.agentId,
                valuePath: mediaSegments.agentId,
            },
        };
    } else if (mediaSegments) {
        updatedMediaSegments = {
            rootPath: mediaSegments.rootPath,
            id: uuidv4(),
            start: {
                id: uuidv4(),
                isRequired: false,
                valuePath: mediaSegments.start,
            },
            end: {
                id: uuidv4(),
                primary: {
                    id: uuidv4(),
                    isRequired: false,
                    valuePath: mediaSegments.endPrimary,
                },
                alternate: {
                    id: uuidv4(),
                    isRequired: false,
                    valuePath: mediaSegments.endAlternate,
                },
            },
            agentName: {
                id: uuidv4(),
                isRequired: false,
                valuePath: mediaSegments.agentName,
            },
            agentId: {
                id: uuidv4(),
                isRequired: false,
                valuePath: mediaSegments.agentId,
            },
        };
    }
    return updatedMediaSegments;
};

export const formatSetupForAPI = (setup: SetupType) => {
    const selectedType = sourceFieldsByType[setup.fileType];

    // cleanup setup for API. Ex: if the field isn't part of the selected type, remove it from the setup

    return {
        metaDataFileType: OrganizationMetadataFileType[setup.fileType],
        metaDataFileTypeExtension:
            OrganizationMetadataFileTypeExtension[setup.fileTypeExtension],
        prefix: selectedType.includes("prefix") ? setup.prefix : undefined,
        nameSpace: selectedType.includes("namespace")
            ? setup.namespace
            : undefined,
        chatFileSchemaHandler: selectedType.includes("chatFileSchemaHandler")
            ? setup.chatFileSchemaHandler
            : undefined,
        agentCodeRequired: setup.isAgentCodeRequired || false,
        isDebug:
            selectedType.includes("isDebug") && setup.sourceType === "batch"
                ? setup.isDebug
                : false,
        name: setup.configurationName,
        csvProperties: setup.delimiter
            ? {
                  delimiter: setup.delimiter,
              }
            : undefined,
    };
};

export const formatCampaignIndicatorsForAPI = (
    campaignIndicators?: CampaignIndicatorType[],
): CampaignIndicatorDefinition[] | undefined => {
    return campaignIndicators?.map((indicator) => {
        return {
            ...indicator,
            campaignIndicatorStrings: indicator.campaignIndicatorStrings.map(
                (string) => string.value,
            ),
        };
    });
};

export const formatLanguageIndicatorsForAPI = (
    languageIndicators?: LanguageIndicatorType[],
): LanguageIndicatorDefinition[] | undefined => {
    return languageIndicators?.map((indicator) => {
        return {
            ...indicator,
            languageIndicatorStrings: indicator.languageIndicatorStrings.map(
                (string) => string.value,
            ),
        };
    });
};
